import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { api } from "@service/api";

import { Masks } from "@hooks/functions/Masks";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { useEffect, useState } from "react";
import {
  ITrackingListDTO,
  trackingStatusSelect,
  TrackingStatusTypes,
} from "@hooks/dtos/ITrackingDTO";
import { Pagination } from "@components/pagination";
import { Card } from "@components/cards/card";
import {
  FaBroom,
  FaEye,
  FaFileExcel,
  FaFileInvoice,
  FaSort,
  FaSortDown,
  FaSortUp,
} from "react-icons/fa";
import { LoadingSkeleton } from "@components/skeleton-loading";
import { TrackingConvertStatus } from "@hooks/functions/Tracking";
import { useNavigate } from "react-router-dom";
import { FaFileCircleExclamation } from "react-icons/fa6";

type IOrdernation = "ASC" | "DESC" | undefined;

const masks = new Masks();
export const CustomerTrackingList = () => {
  const navigate = useNavigate();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  // --- Pagination values --- //
  const [page, setPage] = useState<number>(1);
  const [take, setTake] = useState<number>(10);
  const [trackAmount, setTrackAmount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);

  // --- Filter values --- //
  const [trackingCode, setTrackingCode] = useState<string>("");
  const [status, setStatus] = useState<TrackingStatusTypes | undefined>();
  const [resultTime, setResultTime] = useState("Todo Período");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [orderDate, setOrderDate] = useState<IOrdernation>();
  const [orderOrder, setOrderOrder] = useState<IOrdernation>();

  const [newSearch, setNewSearch] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [trackings, setTrackings] = useState<ITrackingListDTO[]>();

  const cleanFilters = async () => {
    setTrackingCode("");
    setStartDate("");
    setEndDate("");
    setStatus(undefined);
    setPage(1);
    setTotalPages(1);
    setNewSearch(true);
  };

  const buildFilterParams = () => {
    return Object.fromEntries(
      Object.entries({
        ...(status && { status }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(orderDate && { orderDate }),
        ...(orderOrder && { orderOrder }),
        ...(trackingCode && { codeId: trackingCode }),
      }),
    );
  };

  const getCustomerTrackingList = async () => {
    setIsLoading(true);
    try {
      const params = `?${new URLSearchParams(
        Object.entries({
          page: (page || 1).toString(),
          take: take.toString(),
          ...buildFilterParams(),
        }),
      ).toString()}`;
      const result = await api.get(`/customer/tracking/list${params}`);
      setTrackAmount(result.data.total);
      setTrackings(result.data.list);
      setTotalPages(result.data.totalPages || 1);

      setResultTime(
        startDate.length + endDate.length > 0
          ? `Período Selecionado`
          : `Todo Período`,
      );

      setNewSearch(false);
      setIsLoading(false);
    } catch (error: any) {
      console.error(error);
      pushToastify({
        message:
          error?.message || "Houve um erro ao consultar o código informado!",
        type: "error",
        theme: "dark",
        pauseOnHover: true,
      });
    }
  };

  const orderStates = {
    date: { value: orderDate, setter: setOrderDate },
    order: { value: orderOrder, setter: setOrderOrder },
  };

  const handleOrder = (type: keyof typeof orderStates) => {
    const states: IOrdernation[] = [undefined, "ASC", "DESC"];
    const currentState = orderStates[type].value;
    const setState = orderStates[type].setter;

    const currentIndex = states.indexOf(currentState);
    const nextIndex = (currentIndex + 1) % states.length;
    setState(states[nextIndex]);
  };

  const getOrdenationIcon = (ordernation: IOrdernation) => {
    switch (ordernation) {
      case "ASC":
        return <FaSortUp className="ms-1" />;
      case "DESC":
        return <FaSortDown className="ms-1" />;
      default:
        return <FaSort className="ms-1" />;
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const exportTake = 500;

      const firstParams = `?${new URLSearchParams(
        Object.entries({
          page: "1",
          take: exportTake.toString(),
          ...buildFilterParams(),
        }),
      ).toString()}`;
      const firstResult = await api.get(
        `/customer/tracking/list${firstParams}`,
      );
      const allRows: ITrackingListDTO[] = [...firstResult.data.list];
      const exportTotalPages: number = firstResult.data.totalPages;

      if (exportTotalPages > 1) {
        const requests = Array.from(
          { length: exportTotalPages - 1 },
          (_, i) => {
            const p = i + 2;
            const params = `?${new URLSearchParams(
              Object.entries({
                page: p.toString(),
                take: exportTake.toString(),
                ...buildFilterParams(),
              }),
            ).toString()}`;
            return api.get(`/customer/tracking/list${params}`);
          },
        );

        const results = await Promise.all(requests);
        results.forEach((r) => allRows.push(...r.data.list));
      }

      const rows = allRows.map((tracking) => ({
        "Nº Pedido": tracking.code,
        "Cód. Rastreio": (tracking.companyTrackCode || "").toUpperCase(),
        Transportadora: (tracking.company || "").toUpperCase(),
        Destino: `${tracking.cityName} / ${tracking.stateIso}`,
        Status: TrackingConvertStatus(tracking.status).text,
        "Data do Pedido": dayjs(tracking.createdAt).format("DD/MM/YYYY"),
        Invoice: tracking.invoice || "",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [
        { wch: 14 },
        { wch: 20 },
        { wch: 22 },
        { wch: 22 },
        { wch: 18 },
        { wch: 16 },
        { wch: 40 },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Rastreios");
      XLSX.writeFile(
        wb,
        `rastreios_${dayjs().format("YYYY-MM-DD_HH-mm")}.xlsx`,
      );

      pushToastify({
        message: `${allRows.length} registros exportados com sucesso.`,
        type: "success",
        theme: "dark",
        pauseOnHover: true,
      });
    } catch (error: any) {
      pushToastify({
        message: error?.message || "Erro ao exportar os dados.",
        type: "error",
        theme: "dark",
        pauseOnHover: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    getCustomerTrackingList();
  }, [page, orderDate, orderOrder]);

  useEffect(() => {
    if (newSearch) getCustomerTrackingList();
  }, [newSearch]);

  return (
    <>
      {/* Filters Area */}
      <div className="row">
        <div className="col-md-12">
          <Card>
            <div className="card-header">
              <div className="card-title">Filtrar Registros</div>
              <div className="card-category">
                Utilize o filtro para trazer um período específico.
              </div>
            </div>
            <div className="card-body">
              {/* Filters */}
              <div className="row">
                {/* Customer | Client */}
                <div className="col-md-3">
                  <div className="form-group">
                    <label htmlFor="trackingCode">
                      Nº Pedido ou Cód. Rastreio
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      onChange={(e) => {
                        const code = e.target.value;
                        setTrackingCode(code);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          getCustomerTrackingList();
                        }
                      }}
                      id="trackingCode"
                      value={trackingCode}
                    />
                  </div>
                </div>
                {/* Start Date */}
                <div className="col-md-3">
                  <div className="form-group">
                    <label htmlFor="startDate">Data Inicial</label>
                    <input
                      type="date"
                      className="form-control"
                      onChange={(e) => setStartDate(e.target.value)}
                      id="startDate"
                      value={startDate}
                    />
                  </div>
                </div>
                {/* End Date */}
                <div className="col-md-3">
                  <div className="form-group">
                    <label htmlFor="endDate">Data Final</label>
                    <input
                      type="date"
                      className="form-control"
                      onChange={(e) => setEndDate(e.target.value)}
                      id="endDate"
                      value={endDate}
                    />
                  </div>
                </div>
                {/* Status */}
                <div className="col-md-3">
                  <div className="form-group">
                    <label htmlFor="status">Status</label>
                    <select
                      className="form-control"
                      value={status || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setStatus(
                          value === "0"
                            ? undefined
                            : (value as TrackingStatusTypes),
                        );
                      }}
                    >
                      <option value="">Selecione...</option>
                      {trackingStatusSelect.map((select) => {
                        return (
                          <option key={select.status} value={select.status}>
                            {select.text}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>
              {/* Filter Buttons */}
              <div className="row">
                <div className="d-flex justify-content-end">
                  <div className="form-group">
                    <button
                      className="btn btn-sm btn-success form-control px-5"
                      onClick={() => getCustomerTrackingList()}
                    >
                      <span className="text-white">Filtrar</span>
                    </button>
                  </div>
                  <div className="form-group">
                    <button
                      className="btn btn-sm btn-warning form-control"
                      onClick={() => cleanFilters()}
                      title="Limpar todos os filtros."
                    >
                      <FaBroom className="text-white" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <span className="position-absolute end-0 bg-info text-white p-2 rounded-end rounded-bottom-0">
              {resultTime}
            </span>
          </Card>
        </div>
      </div>

      {/* History Logs Table */}
      <div className="row">
        <div className="col-md-12">
          <Card>
            <div className="card-header">
              <div className="row">
                <div className="col-md-10">
                  <h4 className="card-title">Histórico de Rastreios</h4>
                  <p className="card-category">
                    Lista de todos rastreios em seu CPF.
                  </p>
                </div>
                <div className="col-md-2 d-flex justify-content-end align-items-center">
                  <button
                    className="btn btn-sm btn-success"
                    onClick={handleExportExcel}
                    disabled={isExporting || isLoading}
                    title="Exportar para Excel"
                  >
                    {isExporting ? (
                      <span className="spinner-border spinner-border-sm text-white" />
                    ) : (
                      <FaFileExcel className="text-white fs-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <div
                  id="basic-datatables_wrapper"
                  className="dataTables_wrapper container-fluid dt-bootstrap4"
                >
                  <div
                    id="basic-datatables_length"
                    className="dataTables_length"
                  >
                    <table className="table table-striped">
                      <thead>
                        <tr role="row">
                          <th
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              handleOrder("order");
                            }}
                          >
                            Nº Pedido {getOrdenationIcon(orderOrder)}
                          </th>
                          <th>Cód. Rastreio</th>
                          <th title={`Data Responsável pelo Frete`}>
                            Transportadora
                          </th>
                          <th>Destino</th>
                          {/* <th>Estágio</th> */}
                          <th>Status</th>
                          <th
                            className="d-flex align-items-center"
                            title={`Data do Pedido`}
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              handleOrder("date");
                            }}
                          >
                            Data do Pedido
                            {getOrdenationIcon(orderDate)}
                          </th>
                          <th>Detalhes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <tr>
                            <td colSpan={10} className="text-center">
                              <LoadingSkeleton />
                            </td>
                          </tr>
                        ) : (
                          <>
                            {trackings &&
                              trackings.length > 0 &&
                              trackings.map((tracking) => {
                                // const externalStatus =
                                //   TrackingConvertExternalStatus(
                                //     tracking.externalStatus,
                                //   );
                                const status = TrackingConvertStatus(
                                  tracking.status,
                                );
                                return (
                                  <tr key={tracking?.id}>
                                    <td>{`${tracking?.code}`}</td>
                                    <td>
                                      {(
                                        tracking.companyTrackCode || "~~"
                                      ).toLocaleUpperCase()}
                                    </td>
                                    <td>
                                      {(
                                        tracking.company || "~~"
                                      ).toLocaleUpperCase()}
                                    </td>
                                    <td>
                                      {tracking.cityName} / {tracking.stateIso}
                                    </td>
                                    {/* <td title={externalStatus.description}>
                                      <span
                                        className={`fs-6 badge badge-${externalStatus.color}`}
                                      >
                                        {externalStatus.text}
                                      </span>
                                    </td> */}
                                    <td title={status.description}>
                                      <span
                                        className={`fs-6 badge badge-${status.color}`}
                                      >
                                        {status.text}
                                      </span>
                                    </td>
                                    <td>
                                      {dayjs(tracking.createdAt).format(
                                        "DD/MM/YYYY",
                                      )}
                                    </td>
                                    <td className="text-center">
                                      <a
                                        href={`/tracking/${tracking.code}`}
                                        title="Ver Detalhes"
                                        className="badge badge-info"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          navigate(
                                            `/tracking/${tracking.code}`,
                                          );
                                        }}
                                      >
                                        <FaEye className="fs-5" />
                                      </a>

                                      {/* Invoice Button */}
                                      <button
                                        title={
                                          tracking.invoice
                                            ? "Ver Invoice do Pedido"
                                            : "Invoice não disponível"
                                        }
                                        className={`badge badge-${tracking.invoice ? "info" : "secondary"} ms-2`}
                                        disabled={!tracking?.invoice}
                                        onClick={() => {
                                          window.open(
                                            tracking.invoice,
                                            "_blank",
                                          );
                                        }}
                                      >
                                        {tracking.invoice ? (
                                          <FaFileInvoice className="fs-5" />
                                        ) : (
                                          <FaFileCircleExclamation className="fs-5" />
                                        )}
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}

                            {isLoading ||
                              !trackings ||
                              (trackings?.length === 0 && (
                                <tr>
                                  <td colSpan={10} className="text-center">
                                    Nenhum registro encontrado.
                                  </td>
                                </tr>
                              ))}
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <span>
                  Quantidade de Registros: {masks.formatMilhar(trackAmount)}
                </span>
                <Pagination
                  page={page}
                  setPage={setPage}
                  totalPages={totalPages}
                  maxPageShow={5}
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};
